/**
 * Unified HTTP client with authentication, retry logic, and caching
 */

import { API_URL } from "./config";
import { SimpleCache } from "../../utils/performance";

// Global cache instances
export const apiCache = new SimpleCache<any>(5); // 5 minute default TTL
export const longCache = new SimpleCache<any>(30); // 30 minute TTL for site data, periods

interface ApiRequestOptions extends Omit<RequestInit, 'cache'> {
  cacheResponse?: boolean | number; // true = 5min, false = no cache, number = custom minutes
  retries?: number;
  timeout?: number;
}

interface ApiError extends Error {
  status?: number;
  statusText?: string;
}

/**
 * Sleep utility for retry delays
 */
const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Get auth token from localStorage
 */
function getAuthToken(): string | null {
  return localStorage.getItem("token");
}

/**
 * Create an API error with status information
 */
function createApiError(message: string, status?: number, statusText?: string): ApiError {
  const error = new Error(message) as ApiError;
  error.status = status;
  error.statusText = statusText;
  return error;
}

/**
 * Unified fetch wrapper with auth, retry, and error handling
 */
async function fetchWithRetry(
  url: string,
  options: ApiRequestOptions = {},
  attempt: number = 1
): Promise<Response> {
  const { retries = 3, timeout = 30000, ...fetchOptions } = options;
  
  // Add auth header
  const token = getAuthToken();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(fetchOptions.headers as Record<string, string> || {}),
  };
  
  if (token && !headers["Authorization"]) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  // Add timeout using AbortController
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      ...fetchOptions,
      headers,
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    // Handle HTTP errors
    if (!response.ok) {
      // Don't retry 4xx errors (client errors)
      if (response.status >= 400 && response.status < 500) {
        throw createApiError(
          `Request failed: ${response.statusText}`,
          response.status,
          response.statusText
        );
      }

      // Retry 5xx errors (server errors) if retries remaining
      if (attempt < retries) {
        const delay = Math.min(1000 * Math.pow(2, attempt - 1), 5000); // Exponential backoff, max 5s
        console.warn(`Request failed (attempt ${attempt}/${retries}), retrying in ${delay}ms...`);
        await sleep(delay);
        return fetchWithRetry(url, options, attempt + 1);
      }

      throw createApiError(
        `Request failed after ${retries} attempts: ${response.statusText}`,
        response.status,
        response.statusText
      );
    }

    return response;
  } catch (error) {
    clearTimeout(timeoutId);

    // Handle timeout
    if (error instanceof Error && error.name === 'AbortError') {
      throw createApiError(`Request timeout after ${timeout}ms`);
    }

    // Retry network errors if retries remaining
    if (attempt < retries && !(error instanceof Error && (error as ApiError).status)) {
      const delay = Math.min(1000 * Math.pow(2, attempt - 1), 5000);
      console.warn(`Request failed (attempt ${attempt}/${retries}), retrying in ${delay}ms...`);
      await sleep(delay);
      return fetchWithRetry(url, options, attempt + 1);
    }

    throw error;
  }
}

/**
 * Generate cache key from URL and options
 */
function getCacheKey(url: string, options?: ApiRequestOptions): string {
  const method = options?.method || 'GET';
  const body = options?.body ? JSON.stringify(options.body) : '';
  return `${method}:${url}:${body}`;
}

/**
 * GET request with caching support
 */
export async function get<T>(
  endpoint: string,
  options: ApiRequestOptions = {}
): Promise<T> {
  const url = endpoint.startsWith('http') ? endpoint : `${API_URL}${endpoint}`;
  const cacheKey = getCacheKey(url, options);
  
  // Check cache if enabled
  if (options.cacheResponse !== false) {
    const cacheTTL = typeof options.cacheResponse === 'number' ? options.cacheResponse : 5;
    const cache = cacheTTL >= 30 ? longCache : apiCache;
    
    const cached = cache.get(cacheKey);
    if (cached) {
      return cached as T;
    }
  }

  const response = await fetchWithRetry(url, {
    ...options,
    method: 'GET',
  });

  const data = await response.json();

  // Cache successful responses
  if (options.cacheResponse !== false) {
    const cacheTTL = typeof options.cacheResponse === 'number' ? options.cacheResponse : 5;
    const cache = cacheTTL >= 30 ? longCache : apiCache;
    cache.set(cacheKey, data);
  }

  return data as T;
}

/**
 * POST request
 */
export async function post<T>(
  endpoint: string,
  body: any,
  options: ApiRequestOptions = {}
): Promise<T> {
  const url = endpoint.startsWith('http') ? endpoint : `${API_URL}${endpoint}`;

  const response = await fetchWithRetry(url, {
    ...options,
    method: 'POST',
    body: JSON.stringify(body),
  });

  return response.json() as Promise<T>;
}

/**
 * PUT request
 */
export async function put<T>(
  endpoint: string,
  body: any,
  options: ApiRequestOptions = {}
): Promise<T> {
  const url = endpoint.startsWith('http') ? endpoint : `${API_URL}${endpoint}`;

  const response = await fetchWithRetry(url, {
    ...options,
    method: 'PUT',
    body: JSON.stringify(body),
  });

  return response.json() as Promise<T>;
}

/**
 * PATCH request
 */
export async function patch<T>(
  endpoint: string,
  body: any,
  options: ApiRequestOptions = {}
): Promise<T> {
  const url = endpoint.startsWith('http') ? endpoint : `${API_URL}${endpoint}`;

  const response = await fetchWithRetry(url, {
    ...options,
    method: 'PATCH',
    body: JSON.stringify(body),
  });

  return response.json() as Promise<T>;
}

/**
 * DELETE request
 */
export async function del<T = void>(
  endpoint: string,
  options: ApiRequestOptions = {}
): Promise<T> {
  const url = endpoint.startsWith('http') ? endpoint : `${API_URL}${endpoint}`;

  const response = await fetchWithRetry(url, {
    ...options,
    method: 'DELETE',
  });

  // Handle 204 No Content
  if (response.status === 204) {
    return undefined as T;
  }

  return response.json() as Promise<T>;
}

/**
 * Clear all caches
 */
export function clearAllCaches(): void {
  apiCache.clear();
  longCache.clear();
}

/**
 * Invalidate cache by pattern
 */
export function invalidateCache(pattern: string | RegExp): number {
  const shortCount = apiCache.invalidate(pattern);
  const longCount = longCache.invalidate(pattern);
  return shortCount + longCount;
}

/**
 * Export as default client object
 */
export const apiClient = {
  get,
  post,
  put,
  patch,
  delete: del,
  clearAllCaches,
  invalidateCache,
};
